import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { UserAvatar, LanguageSwitcher } from '../shared';
import { useTranslation } from 'react-i18next';

interface HeaderProps {
  onMenuClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { user, artist, logout, isLoading } = useAuth();
  const { unreadNotificationCount } = useData();
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(artist?.avatar);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleAvatarChange = (file: File) => {
    // Create a preview URL for the uploaded image
    const url = URL.createObjectURL(file);
    setAvatarUrl(url);
    // Here you would typically upload the file to your server
    // and update the user's avatar in the database
  };

  const handleLogout = async () => {
    await logout();
    navigate('/'); // Ana sayfaya yönlendir
  };

  return (
    <header className="backdrop-blur-lg bg-white/10 border-b border-white/20 shadow-lg">
      <div className="flex items-center px-4 lg:px-6 py-4">
        {/* Mobile menu button */}
        <button
          onClick={onMenuClick}
          className="lg:hidden p-2 rounded-xl text-white/70 hover:text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all duration-200"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        {/* Right side - All elements with consistent height, pushed to far right */}
        <div className="flex items-center space-x-4 ml-auto">
          {/* Language Switcher */}
          <LanguageSwitcher />

          {/* Notifications */}
          <button 
            onClick={() => navigate('/notifications')}
            className="h-12 w-12 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all duration-200 relative"
            title={t('navigation.notifications')}
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.73 21a2 2 0 01-3.46 0" />
            </svg>
            {/* Notification badge */}
            {unreadNotificationCount > 0 && (
              <div className="absolute -top-1 -right-1 min-w-5 h-5 bg-orange-500 rounded-full border-2 border-white/20 flex items-center justify-center text-xs text-white font-bold">
                {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
              </div>
            )}
          </button>

          {/* Profile section */}
          <div className="h-12 flex items-center space-x-3 bg-white/10 rounded-xl px-4 py-2 backdrop-blur-sm border border-white/20">
            <div className="hidden md:block text-right">
              <p className="text-sm font-medium text-white">
                {artist?.display_name || user?.email?.split('@')[0]}
              </p>
              <p className="text-xs text-white/60 capitalize">
                {user?.role === 'artist' ? 'Artist' : 'Admin'}
              </p>
            </div>
            <UserAvatar
              src={avatarUrl}
              alt="Profil"
              size="md"
              editable={true}
              onImageChange={handleAvatarChange}
            />
          </div>

          {/* Logout button */}
          <button
            onClick={handleLogout}
            disabled={isLoading}
            className="h-12 w-12 flex items-center justify-center text-white/70 hover:text-red-400 hover:bg-red-500/10 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 transition-all duration-200"
            title={t('navigation.logout')}
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header; 